import React, { useState, useEffect } from 'react';
import { CountryLabel } from '../../../shared/components/CountryFlag';
import {
    Card,
    Row,
    Col,
    Statistic,
    Select,
    Space,
    Typography,
    Table,
    Empty,
    Spin,
    message,
    Divider,
    DatePicker,
    Tag,
    Tabs
} from 'antd';
import {
    LineChartOutlined,
    SearchOutlined,
    GlobalOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    CloudServerOutlined,
    ApiOutlined
} from '@ant-design/icons';
import dnsService from '../../services/dnsService';
import dayjs from 'dayjs';

const { Text, Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

// DNS record type names
const QTYPE_NAMES = {
    1: 'A',
    2: 'NS',
    5: 'CNAME',
    6: 'SOA',
    12: 'PTR',
    15: 'MX',
    16: 'TXT',
    28: 'AAAA',
    33: 'SRV',
    43: 'DS',
    46: 'RRSIG',
    47: 'NSEC',
    48: 'DNSKEY',
    257: 'CAA'
};

// DNS response code names
const RCODE_NAMES = {
    0: 'NOERROR',
    1: 'FORMERR',
    2: 'SERVFAIL',
    3: 'NXDOMAIN',
    4: 'NOTIMP',
    5: 'REFUSED'
};

const DnsAnalytics = () => {
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState([
        dayjs().subtract(24, 'hour'),
        dayjs()
    ]);

    // Zone filter
    // 从 URL 的 ?zone_id= 初始化选中区域（此前未读取，导致带 zone_id 进入时不带 zone 过滤）
    const [selectedZone, setSelectedZone] = useState(() => {
        try {
            const z = new URLSearchParams(window.location.search).get('zone_id');
            return z ? Number(z) : null;
        } catch (e) {
            return null;
        }
    });
    const [zones, setZones] = useState([]);

    // Overview data (both DNS server and proxy)
    const [overview, setOverview] = useState(null);

    // DNS Server stats
    const [dnsStats, setDnsStats] = useState({
        total_queries: 0,
        unique_qnames: 0,
        avg_response_time: 0,
        rcode_distribution: {},
        topQnames: [],
        qtypeDistribution: [],
        geoDistribution: []
    });

    // Proxy stats
    const [proxyStats, setProxyStats] = useState({
        total_queries: 0,
        unique_qnames: 0,
        avg_response_time: 0,
        rcode_distribution: {},
        topQnames: [],
        qtypeDistribution: [],
        blockedQnames: [],
        queryTrend: []
    });

    useEffect(() => {
        loadZones();
    }, []);

    useEffect(() => {
        if (dateRange && dateRange[0] && dateRange[1]) {
            loadAllStats();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange, selectedZone, zones]);

    const loadZones = async () => {
        try {
            const result = await dnsService.getDnsZones();
            if (result.success) {
                setZones(result.data || []);
            }
        } catch (error) {
            console.error('Failed to load zones:', error);
        }
    };

    const loadAllStats = async () => {
        setLoading(true);
        try {
            const startTime = dateRange[0].unix();
            const endTime = dateRange[1].unix();
            const params = { start_time: startTime, end_time: endTime };

            if (selectedZone) {
                const zone = zones.find(z => Number(z.id) === Number(selectedZone));
                if (zone) {
                    params.zone = zone.zone;
                }
            }

            // Get overview and detailed stats
            const [
                overviewRes, statsRes, proxyRes, topQnamesRes, qtypeRes, geoRes,
                proxyTopQnamesRes, proxyQtypeRes, proxyBlockedRes, proxyTrendRes
            ] = await Promise.all([
                dnsService.getAnalyticsOverview(params),
                dnsService.getAnalyticsStats(params),
                dnsService.getAnalyticsProxyStats(params),
                dnsService.getTopQnames({ ...params, limit: 20 }),
                dnsService.getQtypeDistribution(params),
                dnsService.getGeoDistribution(params),
                dnsService.getProxyTopQnames({ ...params, limit: 20 }),
                dnsService.getProxyQtypeDistribution(params),
                dnsService.getProxyBlocked(params),
                dnsService.getProxyQueryTrend({ ...params, interval: '1h' })
            ]);

            if (overviewRes.success) {
                setOverview(overviewRes.data);
            }

            setDnsStats({
                total_queries: statsRes.success ? statsRes.data?.total_queries || 0 : 0,
                unique_qnames: statsRes.success ? statsRes.data?.unique_qnames || 0 : 0,
                avg_response_time: statsRes.success ? statsRes.data?.avg_response_time || 0 : 0,
                rcode_distribution: statsRes.success ? statsRes.data?.rcode_distribution || {} : {},
                topQnames: topQnamesRes.success ? topQnamesRes.data || [] : [],
                qtypeDistribution: qtypeRes.success ? qtypeRes.data || [] : [],
                geoDistribution: geoRes.success ? geoRes.data || [] : []
            });

            setProxyStats({
                total_queries: proxyRes.success ? proxyRes.data?.total_queries || 0 : 0,
                unique_qnames: proxyRes.success ? proxyRes.data?.unique_qnames || 0 : 0,
                avg_response_time: proxyRes.success ? proxyRes.data?.avg_response_time || 0 : 0,
                rcode_distribution: proxyRes.success ? proxyRes.data?.rcode_distribution || {} : {},
                topQnames: proxyTopQnamesRes.success ? proxyTopQnamesRes.data || [] : [],
                qtypeDistribution: proxyQtypeRes.success ? proxyQtypeRes.data || [] : [],
                blockedQnames: proxyBlockedRes.success ? (proxyBlockedRes.data?.top_blocked_qnames || []) : [],
                queryTrend: proxyTrendRes.success ? proxyTrendRes.data || [] : []
            });
        } catch (error) {
            message.error('Failed to load analytics: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const formatNumber = (num) => {
        return new Intl.NumberFormat('zh-CN').format(num || 0);
    };

    // Calculate success rate from rcode distribution
    const getSuccessRate = (rcodeDistribution) => {
        const total = Object.values(rcodeDistribution).reduce((a, b) => a + b, 0);
        if (total === 0) return 0;
        const success = rcodeDistribution['NOERROR'] || rcodeDistribution[0] || 0;
        return ((success / total) * 100).toFixed(2);
    };

    const topQnamesColumns = [
        {
            title: 'Query Name',
            dataIndex: 'qname',
            key: 'qname',
            ellipsis: true,
            render: (text) => <Text code>{text}</Text>
        },
        {
            title: 'Count',
            dataIndex: 'count',
            key: 'count',
            width: 120,
            render: (value) => formatNumber(value),
            sorter: (a, b) => a.count - b.count
        }
    ];

    const qtypeColumns = [
        {
            title: 'Record Type',
            dataIndex: 'qtype',
            key: 'qtype',
            render: (qtype) => (
                <Tag color="blue">{QTYPE_NAMES[qtype] || qtype}</Tag>
            )
        },
        {
            title: 'Count',
            dataIndex: 'count',
            key: 'count',
            render: (value) => formatNumber(value),
            sorter: (a, b) => a.count - b.count
        }
    ];

    const geoColumns = [
        {
            title: 'Country/Region',
            dataIndex: 'country',
            key: 'country',
            render: (text) => (text ? <CountryLabel code={text}>{text}</CountryLabel> : 'Unknown')
        },
        {
            title: 'Count',
            dataIndex: 'count',
            key: 'count',
            render: (value) => formatNumber(value),
            sorter: (a, b) => a.count - b.count
        }
    ];

    const rcodeColumns = [
        {
            title: 'Response Code',
            dataIndex: 'rcode',
            key: 'rcode',
            render: (rcode) => {
                const name = RCODE_NAMES[rcode] || rcode;
                const color = rcode === 0 || rcode === 'NOERROR' ? 'success' :
                             rcode === 3 || rcode === 'NXDOMAIN' ? 'warning' : 'error';
                return <Tag color={color}>{name}</Tag>;
            }
        },
        {
            title: 'Count',
            dataIndex: 'count',
            key: 'count',
            render: (value) => formatNumber(value)
        }
    ];

    const trendColumns = [
        {
            title: 'Time',
            dataIndex: 'timestamp',
            key: 'timestamp',
            render: (ts) => dayjs.unix(ts).format('YYYY-MM-DD HH:mm')
        },
        {
            title: 'Queries',
            dataIndex: 'count',
            key: 'count',
            render: (value) => formatNumber(value)
        }
    ];

    // Convert rcode_distribution object to array for table
    const dnsRcodeData = Object.entries(dnsStats.rcode_distribution).map(([rcode, count]) => ({
        rcode,
        count
    }));

    const proxyRcodeData = Object.entries(proxyStats.rcode_distribution).map(([rcode, count]) => ({
        rcode,
        count
    }));

    const dnsSuccessRate = getSuccessRate(dnsStats.rcode_distribution);
    const proxySuccessRate = getSuccessRate(proxyStats.rcode_distribution);

    return (
        <div>
            <Card
                title="DNS Analytics"
                extra={
                    <Space>
                        <Select
                            showSearch
                            allowClear
                            style={{ width: 250 }}
                            placeholder="Filter by zone (all)"
                            value={selectedZone}
                            onChange={setSelectedZone}
                            filterOption={(input, option) =>
                                option.children.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {zones.map(zone => (
                                <Option key={zone.id} value={zone.id}>
                                    {zone.display_zone || zone.zone}
                                </Option>
                            ))}
                        </Select>
                        <RangePicker
                            value={dateRange}
                            onChange={setDateRange}
                            showTime
                            format="YYYY-MM-DD HH:mm:ss"
                            placeholder={['Start Time', 'End Time']}
                        />
                    </Space>
                }
            >
                <Spin spinning={loading}>
                    {/* Combined Overview */}
                    {overview && (
                        <>
                            <Title level={5}>Overview</Title>
                            <Row gutter={16} style={{ marginBottom: 24 }}>
                                <Col xs={24} sm={12} md={6}>
                                    <Card>
                                        <Statistic
                                            title="Total DNS Queries"
                                            value={formatNumber(overview.combined?.total_queries || 0)}
                                            prefix={<SearchOutlined />}
                                            valueStyle={{ color: '#1890ff' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Card>
                                        <Statistic
                                            title="DNS Server Queries"
                                            value={formatNumber(overview.dns_server?.total_queries || 0)}
                                            prefix={<CloudServerOutlined />}
                                            valueStyle={{ color: '#52c41a' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Card>
                                        <Statistic
                                            title="DNS Proxy Queries"
                                            value={formatNumber(overview.dns_proxy?.total_queries || 0)}
                                            prefix={<ApiOutlined />}
                                            valueStyle={{ color: '#722ed1' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Card>
                                        <Statistic
                                            title="Unique Domains"
                                            value={formatNumber(overview.combined?.unique_qnames || 0)}
                                            prefix={<GlobalOutlined />}
                                            valueStyle={{ color: '#fa8c16' }}
                                        />
                                    </Card>
                                </Col>
                            </Row>
                            <Divider />
                        </>
                    )}

                    <Tabs defaultActiveKey="dns-server">
                        <TabPane tab={<span><CloudServerOutlined /> DNS Server</span>} key="dns-server">
                            {/* DNS Server Stats */}
                            <Row gutter={16} style={{ marginBottom: 24 }}>
                                <Col xs={24} sm={12} md={6}>
                                    <Card>
                                        <Statistic
                                            title="Total Queries"
                                            value={formatNumber(dnsStats.total_queries)}
                                            prefix={<SearchOutlined />}
                                            valueStyle={{ color: '#1890ff' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Card>
                                        <Statistic
                                            title="Unique QNames"
                                            value={formatNumber(dnsStats.unique_qnames)}
                                            prefix={<GlobalOutlined />}
                                            valueStyle={{ color: '#722ed1' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Card>
                                        <Statistic
                                            title="Success Rate"
                                            value={dnsSuccessRate}
                                            suffix="%"
                                            prefix={dnsSuccessRate >= 95 ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                                            valueStyle={{ color: dnsSuccessRate >= 95 ? '#3f8600' : '#cf1322' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Card>
                                        <Statistic
                                            title="Avg Response Time"
                                            value={dnsStats.avg_response_time}
                                            suffix="ms"
                                            prefix={<LineChartOutlined />}
                                            valueStyle={{ color: dnsStats.avg_response_time < 100 ? '#3f8600' : '#cf1322' }}
                                        />
                                    </Card>
                                </Col>
                            </Row>

                            <Row gutter={[16, 16]}>
                                <Col xs={24} lg={12}>
                                    <Card title="Top Query Names" size="small">
                                        {dnsStats.topQnames.length > 0 ? (
                                            <Table
                                                columns={topQnamesColumns}
                                                dataSource={dnsStats.topQnames}
                                                rowKey="qname"
                                                pagination={{ pageSize: 10 }}
                                                size="small"
                                            />
                                        ) : (
                                            <Empty description="No data" />
                                        )}
                                    </Card>
                                </Col>

                                <Col xs={24} lg={12}>
                                    <Card title="Query Type Distribution" size="small">
                                        {dnsStats.qtypeDistribution.length > 0 ? (
                                            <Table
                                                columns={qtypeColumns}
                                                dataSource={dnsStats.qtypeDistribution}
                                                rowKey="qtype"
                                                pagination={{ pageSize: 10 }}
                                                size="small"
                                            />
                                        ) : (
                                            <Empty description="No data" />
                                        )}
                                    </Card>
                                </Col>

                                <Col xs={24} lg={12}>
                                    <Card title="Geo Distribution" size="small">
                                        {dnsStats.geoDistribution.length > 0 ? (
                                            <Table
                                                columns={geoColumns}
                                                dataSource={dnsStats.geoDistribution}
                                                rowKey="country"
                                                pagination={{ pageSize: 10 }}
                                                size="small"
                                            />
                                        ) : (
                                            <Empty description="No data" />
                                        )}
                                    </Card>
                                </Col>

                                <Col xs={24} lg={12}>
                                    <Card title="Response Code Distribution" size="small">
                                        {dnsRcodeData.length > 0 ? (
                                            <Table
                                                columns={rcodeColumns}
                                                dataSource={dnsRcodeData}
                                                rowKey="rcode"
                                                pagination={{ pageSize: 10 }}
                                                size="small"
                                            />
                                        ) : (
                                            <Empty description="No data" />
                                        )}
                                    </Card>
                                </Col>
                            </Row>
                        </TabPane>

                        <TabPane tab={<span><ApiOutlined /> DNS Proxy</span>} key="dns-proxy">
                            {/* DNS Proxy Stats */}
                            <Row gutter={16} style={{ marginBottom: 24 }}>
                                <Col xs={24} sm={12} md={6}>
                                    <Card>
                                        <Statistic
                                            title="Total Queries"
                                            value={formatNumber(proxyStats.total_queries)}
                                            prefix={<SearchOutlined />}
                                            valueStyle={{ color: '#1890ff' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Card>
                                        <Statistic
                                            title="Unique QNames"
                                            value={formatNumber(proxyStats.unique_qnames)}
                                            prefix={<GlobalOutlined />}
                                            valueStyle={{ color: '#722ed1' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Card>
                                        <Statistic
                                            title="Success Rate"
                                            value={proxySuccessRate}
                                            suffix="%"
                                            prefix={proxySuccessRate >= 95 ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                                            valueStyle={{ color: proxySuccessRate >= 95 ? '#3f8600' : '#cf1322' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Card>
                                        <Statistic
                                            title="Avg Response Time"
                                            value={proxyStats.avg_response_time}
                                            suffix="ms"
                                            prefix={<LineChartOutlined />}
                                            valueStyle={{ color: proxyStats.avg_response_time < 100 ? '#3f8600' : '#cf1322' }}
                                        />
                                    </Card>
                                </Col>
                            </Row>

                            <Row gutter={[16, 16]}>
                                <Col xs={24} lg={12}>
                                    <Card title="Top Query Names" size="small">
                                        {proxyStats.topQnames.length > 0 ? (
                                            <Table
                                                columns={topQnamesColumns}
                                                dataSource={proxyStats.topQnames}
                                                rowKey="qname"
                                                pagination={{ pageSize: 10 }}
                                                size="small"
                                            />
                                        ) : (
                                            <Empty description="No data" />
                                        )}
                                    </Card>
                                </Col>

                                <Col xs={24} lg={12}>
                                    <Card title="Query Type Distribution" size="small">
                                        {proxyStats.qtypeDistribution.length > 0 ? (
                                            <Table
                                                columns={qtypeColumns}
                                                dataSource={proxyStats.qtypeDistribution}
                                                rowKey="qtype"
                                                pagination={{ pageSize: 10 }}
                                                size="small"
                                            />
                                        ) : (
                                            <Empty description="No data" />
                                        )}
                                    </Card>
                                </Col>

                                <Col xs={24} lg={12}>
                                    <Card title="Top Blocked Domains" size="small">
                                        {proxyStats.blockedQnames.length > 0 ? (
                                            <Table
                                                columns={topQnamesColumns}
                                                dataSource={proxyStats.blockedQnames}
                                                rowKey="qname"
                                                pagination={{ pageSize: 10 }}
                                                size="small"
                                            />
                                        ) : (
                                            <Empty description="No blocked queries" />
                                        )}
                                    </Card>
                                </Col>

                                <Col xs={24} lg={12}>
                                    <Card title="Response Code Distribution" size="small">
                                        {proxyRcodeData.length > 0 ? (
                                            <Table
                                                columns={rcodeColumns}
                                                dataSource={proxyRcodeData}
                                                rowKey="rcode"
                                                pagination={{ pageSize: 10 }}
                                                size="small"
                                            />
                                        ) : (
                                            <Empty description="No data" />
                                        )}
                                    </Card>
                                </Col>

                                <Col xs={24}>
                                    <Card title="Query Trend" size="small">
                                        {proxyStats.queryTrend.length > 0 ? (
                                            <Table
                                                columns={trendColumns}
                                                dataSource={proxyStats.queryTrend}
                                                rowKey="timestamp"
                                                pagination={{ pageSize: 12 }}
                                                size="small"
                                            />
                                        ) : (
                                            <Empty description="No data" />
                                        )}
                                    </Card>
                                </Col>
                            </Row>
                        </TabPane>
                    </Tabs>
                </Spin>
            </Card>
        </div>
    );
};

export default DnsAnalytics;
